// 📱 MODO APP (perfiles.modo_app, 733): interfaz mínima tipo app móvil.
// Sin sidebar, sin header del sistema, sin pestañas ni selectores de tema/idioma:
// solo la pantalla de trabajo, una barra superior mínima (empresa · usuario ·
// Salir) y, si el perfil tiene más de una opción, una barra inferior de
// navegación tipo app. Pensado para el perfil Mesero (Mesero móvil).
import { useEffect, useState } from 'react';
import { ChevronLeft, LogOut, MoreHorizontal } from 'lucide-react';
import { marca } from '@brand';
import { useTranslation } from '@/lib/hooks/useTranslation';
import * as Icons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/lib/hooks';
import { useTabsStore } from '@/stores/tabs.store';

export interface OpcionApp { ruta: string; nombre: string; icon: string | null }

const iconDe = (nombre: string | null): LucideIcon => {
  const lib = Icons as unknown as Record<string, LucideIcon>;
  return (nombre && lib[nombre]) || Icons.Circle;
};

export function ModoAppShell({ empresa, opciones, children }: {
  empresa: string; opciones: OpcionApp[]; children: React.ReactNode;
}) {
  const { user, logout, isLoggingOut } = useAuth();

  // Pantalla siempre encendida mientras la app está visible (Wake Lock; se
  // re-adquiere al volver del background). Best-effort: si el navegador no lo
  // soporta (iOS viejo), no pasa nada.
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const pedir = async () => {
      try {
        const wl = (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock;
        if (wl && document.visibilityState === 'visible') lock = await wl.request('screen');
      } catch { /* sin soporte o denegado */ }
    };
    void pedir();
    const onVis = () => { if (document.visibilityState === 'visible') void pedir(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); void lock?.release().catch(() => {}); };
  }, []);
  const { tabs, activeTabId, openTab } = useTabsStore();
  const activa = tabs.find((t) => t.id === activeTabId)?.path;
  const { t } = useTranslation();
  const [mas, setMas] = useState(false);

  // Barra inferior: la marca puede fijar sus rutas principales (TrackConstruction: Hoy ·
  // Bitácora · Horas · Punch). Con más opciones de las que caben: 4 + «Más» (antes se
  // cortaba en 5 y el resto quedaba inalcanzable).
  const preferidas = marca.barraApp?.map((r) => opciones.find((o) => o.ruta === r)).filter((o): o is OpcionApp => !!o) ?? [];
  const base = preferidas.length ? [...preferidas, ...opciones.filter((o) => !preferidas.includes(o))] : opciones;
  const conMas = base.length > 5 || !!marca.SelectorContexto;
  const barra = conMas ? base.slice(0, 4) : base;
  // Lo flotante (asistente, orbe de voz) se monta fuera del shell: se le avisa la altura de la
  // barra inferior para que no la tape (el asistente tapaba «Más» en el teléfono).
  const hayBarra = opciones.length > 1 || conMas;
  useEffect(() => {
    const raiz = document.documentElement;
    if (hayBarra) raiz.style.setProperty('--alto-barra-app', '64px'); else raiz.style.removeProperty('--alto-barra-app');
    raiz.dataset.modoApp = '1';   // la marca puede ajustar lo flotante en modo app (Track lo oculta)
    return () => { raiz.style.removeProperty('--alto-barra-app'); delete raiz.dataset.modoApp; };
  }, [hayBarra]);
  const ir = (o: OpcionApp) => { setMas(false); openTab({ id: o.ruta, title: o.nombre, path: o.ruta, icon: o.icon || undefined }); };

  const salir = () => {
    // limpia la sesión de mesero (PIN) además del token
    try { sessionStorage.removeItem('rest_mesero'); } catch { /* noop */ }
    logout();
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-background overscroll-none" style={{ touchAction: 'manipulation' }}>
      {marca.SelectorContexto ? (
        // Marca con contexto (TrackConstruction): obra actual + conexión; «Salir» va en «Más»
        <header className="flex items-center gap-2 border-b bg-card px-3 py-2">
          <marca.SelectorContexto compacto />
        </header>
      ) : (
        <header className="flex items-center gap-2 border-b bg-card px-3 py-2">
          <span className="min-w-0 truncate text-sm font-semibold">{empresa}</span>
          <span className="min-w-0 truncate text-xs text-muted-foreground">· {user?.username ?? ''}</span>
          <button
            className="ml-auto inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs"
            onClick={salir} disabled={isLoggingOut} title="Cerrar sesión">
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </header>
      )}

      <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</main>

      {mas && (
        // «Más»: vista completa integrada (sin modal), con Volver
        <div className="fixed inset-0 z-[60] flex flex-col bg-background">
          <div className="flex items-center gap-2 border-b px-2 py-2">
            <button type="button" onClick={() => setMas(false)} className="flex min-h-[44px] items-center gap-1 px-2 text-sm font-medium">
              <ChevronLeft className="h-5 w-5" />{t('common.back')}
            </button>
            <span className="flex-1 truncate px-2 text-right text-xs text-muted-foreground">{empresa} · {user?.username ?? ''}</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {base.slice(4).map((o) => {
              const Icono = iconDe(o.icon);
              return (
                <button key={o.ruta} type="button" onClick={() => ir(o)}
                  className={`flex min-h-[56px] w-full items-center gap-4 border-b px-5 text-left text-base ${activa === o.ruta ? 'font-semibold text-primary' : ''}`}>
                  <Icono className="h-5 w-5 text-muted-foreground" />{o.nombre}
                </button>
              );
            })}
            <button type="button" onClick={salir} disabled={isLoggingOut}
              className="flex min-h-[56px] w-full items-center gap-4 px-5 text-left text-base text-destructive">
              <LogOut className="h-5 w-5" />{t('shell.logout')}
            </button>
          </div>
        </div>
      )}

      {hayBarra && (
        <nav className="flex border-t bg-card pb-[env(safe-area-inset-bottom)]">
          {barra.map((o) => {
            const Icono = iconDe(o.icon);
            const act = activa === o.ruta && !mas;
            return (
              <button key={o.ruta}
                className={`flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] ${act ? 'text-primary font-medium' : 'text-muted-foreground'}`}
                onClick={() => ir(o)}>
                <Icono className="h-5 w-5" />
                <span className="max-w-full truncate px-1">{o.nombre}</span>
              </button>
            );
          })}
          {conMas && (
            <button type="button" onClick={() => setMas(true)}
              className={`flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] ${mas ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              <MoreHorizontal className="h-5 w-5" />
              <span>{t('shell.mas')}</span>
            </button>
          )}
        </nav>
      )}
    </div>
  );
}
